import { useEffect, useMemo, useState } from 'react';
import { IProductionCycle } from 'src/pages/accueil/model/types';
import { useHttpClient } from 'src/security/hooks/axiosProvider';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface FilterOption {
  id: number;
  name: string;
}

/** Shape matches Spring Page — drop-in compatible when backend supports filtering. */
export interface ProductionCyclesPage {
  content: IProductionCycle[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface ProductionCyclesOptions {
  tpes: FilterOption[];
  productions: FilterOption[];
}

export interface UseProductionCyclesParams {
  search: string;
  countryId: number | null;
  projectId: number | null;
  tpeId: number | null;
  productionId: number | null;
  page: number;
  size: number;
}

// ─── Frontend helpers (temporary — remove when backend handles filtering) ─────

const getCountry = (pc: IProductionCycle): FilterOption | null => pc.tpe?.project?.country ?? null;

const applyFilters = (
  cycles: IProductionCycle[],
  { search, countryId, projectId, tpeId, productionId }: Omit<UseProductionCyclesParams, 'page' | 'size'>
): IProductionCycle[] =>
  cycles.filter(
    (pc) =>
      (!search ||
        pc.label?.toUpperCase().includes(search.toUpperCase()) ||
        pc.mode?.toUpperCase().includes(search.toUpperCase())) &&
      (countryId == null || getCountry(pc)?.id === countryId) &&
      (projectId == null || pc.tpe?.project?.id === projectId) &&
      (tpeId == null || pc.tpe?.id === tpeId) &&
      (productionId == null || pc.production?.id === productionId)
  );

const buildOptions = (
  cycles: IProductionCycle[],
  projectId: number | null,
  tpeId: number | null
): ProductionCyclesOptions => ({
  tpes: Array.from(
    new Map(
      cycles
        .filter((pc) => projectId == null || pc.tpe?.project?.id === projectId)
        .filter((pc) => pc.tpe?.id != null && pc.tpe?.name)
        .map((pc) => [pc.tpe!.id as number, { id: pc.tpe!.id as number, name: pc.tpe!.name }])
    ).values()
  ),
  productions: Array.from(
    new Map(
      cycles
        .filter((pc) => tpeId == null || pc.tpe?.id === tpeId)
        .filter((pc) => pc.production?.id != null && pc.production?.name)
        .map((pc) => [
          pc.production.id as number,
          { id: pc.production.id as number, name: pc.production.name as string },
        ])
    ).values()
  ),
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useProductionCycles = ({
  search,
  countryId,
  projectId,
  tpeId,
  productionId,
  page,
  size,
}: UseProductionCyclesParams) => {
  const [allCycles, setAllCycles] = useState<IProductionCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const httpClient = useHttpClient(true);

  // Fetch all once.
  // TODO (backend): replace with reactive fetch using query params:
  //   GET /api/production-cycle?search=&countryId=&projectId=&tpeId=&productionId=&page=&size=
  //   Then use the paginated response directly and remove applyFilters + buildOptions.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await httpClient.current?.get('/api/production-cycle');
        setAllCycles(res?.data || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch production cycles');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [httpClient]);

  const filtered = useMemo(
    () => applyFilters(allCycles, { search, countryId, projectId, tpeId, productionId }),
    [allCycles, search, countryId, projectId, tpeId, productionId]
  );

  const data: ProductionCyclesPage = useMemo(() => {
    const start = page * size;
    return {
      content: filtered.slice(start, start + size),
      page,
      size,
      totalElements: filtered.length,
      totalPages: Math.ceil(filtered.length / size),
    };
  }, [filtered, page, size]);

  const options: ProductionCyclesOptions = useMemo(
    () => buildOptions(allCycles, projectId, tpeId),
    [allCycles, projectId, tpeId]
  );

  return { data, loading, error, options };
};
